/* ============================================================================
   FLIT — shared interactive behaviors (mobile-first)
   Vanilla JS, no dependencies. Each behavior is opt-in via data-attributes
   so sections stay independent and theme-editor friendly.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- Filter tabs: switch visible panel by data-flit-tab ----
     Markup:
       <div data-flit-tabs>
         <div class="flit-tabs" role="tablist">
           <button class="flit-tab" data-target="all" aria-selected="true">All</button> ...
         </div>
         <div data-flit-panels>
           <div data-panel="all">...</div> ...
         </div>
       </div>
  */
  function initTabs(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('.flit-tab'));
    var panelWrap = root.querySelector('[data-flit-panels]');
    if (!tabs.length) return;
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) { t.setAttribute('aria-selected', String(t === tab)); });
        tab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        if (!panelWrap) return;
        var target = tab.getAttribute('data-target');
        Array.prototype.forEach.call(panelWrap.querySelectorAll('[data-panel]'), function (p) {
          p.hidden = p.getAttribute('data-panel') !== target;
        });
      });
    });
  }

  /* ---- Carousel progress bar: reflects horizontal scroll position ----
     Markup: a .flit-rail (or [data-flit-track]) followed/related to a
     [data-flit-progress] containing .flit-progress__bar.
  */
  function initProgress(rail) {
    var wrap = rail.closest('[data-flit-carousel]') || rail.parentNode;
    var bar = wrap && wrap.querySelector('.flit-progress__bar');
    if (!bar) return;
    var update = function () {
      var max = rail.scrollWidth - rail.clientWidth;
      var ratio = max > 0 ? rail.scrollLeft / max : 0;
      var travel = (bar.parentNode.offsetWidth || 0) - bar.offsetWidth;
      bar.style.transform = 'translateX(' + (ratio * travel) + 'px)';
    };
    rail.addEventListener('scroll', function () {
      window.requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---- Accordion (footer link groups) ----
     Markup:
       <div class="flit-accordion">
         <button class="flit-accordion__head" aria-expanded="false">Group</button>
         <div class="flit-accordion__body" hidden>...</div>
       </div>
  */
  function initAccordion(acc) {
    var head = acc.querySelector('.flit-accordion__head');
    var body = acc.querySelector('.flit-accordion__body');
    if (!head || !body) return;
    head.addEventListener('click', function () {
      var open = head.getAttribute('aria-expanded') === 'true';
      head.setAttribute('aria-expanded', String(!open));
      body.hidden = open;
    });
  }

  /* ---- Rotating search placeholder ----
     Markup: <input data-flit-rotate='["Search For Nike","Search For Jordan"]'>
  */
  function initRotatingPlaceholder(input) {
    var list;
    try { list = JSON.parse(input.getAttribute('data-flit-rotate') || '[]'); }
    catch (e) { list = []; }
    if (!list.length) return;
    var i = 0;
    input.setAttribute('placeholder', list[0]);
    setInterval(function () {
      if (document.activeElement === input) return; // don't fight the user
      i = (i + 1) % list.length;
      input.setAttribute('placeholder', list[i]);
    }, 2500);
  }

  /* ---- In-card product image carousel (swipe + dots) ----
     Markup:
       <div data-flit-cardimgs>
         <div class="flit-cardimgs__track">
           <img> <img> ...
         </div>
         <div class="flit-cardimgs__dots"></div>
       </div>
  */
  function initCardImages(card) {
    var track = card.querySelector('.flit-cardimgs__track');
    var dotsWrap = card.querySelector('.flit-cardimgs__dots');
    if (!track) return;
    var slides = track.children.length;
    if (dotsWrap && slides > 1) {
      for (var d = 0; d < slides; d++) {
        var dot = document.createElement('span');
        dot.className = 'flit-cardimgs__dot' + (d === 0 ? ' is-active' : '');
        dotsWrap.appendChild(dot);
      }
      track.addEventListener('scroll', function () {
        var idx = Math.round(track.scrollLeft / track.clientWidth);
        Array.prototype.forEach.call(dotsWrap.children, function (el, n) {
          el.classList.toggle('is-active', n === idx);
        });
      }, { passive: true });
    }
  }

  /* ---- Product page: size selection, sticky bar, NOW checker ---- */
  function initPdp(pdp) {
    var priceEls = pdp.querySelectorAll('[data-flit-price]');
    var varInput = pdp.querySelector('[data-flit-varid]');
    var atc = pdp.querySelector('[data-flit-atc]');
    var atcLabel = pdp.querySelector('[data-flit-atc-label]');
    var sizes = Array.prototype.slice.call(pdp.querySelectorAll('.flit-pdp__size'));

    sizes.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('data-soldout') === 'true') return;
        sizes.forEach(function (b) { b.setAttribute('aria-pressed', String(b === btn)); });
        var id = btn.getAttribute('data-variant-id');
        var price = btn.getAttribute('data-price');
        var available = btn.getAttribute('data-available') === 'true';
        if (varInput) { varInput.value = id; varInput.disabled = !available; }
        if (price) priceEls.forEach(function (el) { el.textContent = price; });
        if (atc) {
          atc.disabled = !available;
          if (atcLabel) atcLabel.innerHTML = available ? 'Add To Cart &rarr;' : 'Sold Out';
        }
      });
    });

    // Sticky bar: show once the main Add-to-Cart scrolls out of view
    var sticky = pdp.querySelector('[data-flit-sticky]');
    var stickyBtn = pdp.querySelector('[data-flit-sticky-btn]');
    if (sticky && atc && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { sticky.hidden = e.isIntersecting; });
      }, { rootMargin: '0px 0px -10% 0px' });
      io.observe(atc);
    }
    if (stickyBtn && atc) {
      stickyBtn.addEventListener('click', function () {
        if (atc.disabled) return;
        atc.click();
      });
    }

    // NOW delivery checker (mock estimate for v1)
    var now = pdp.querySelector('[data-flit-now]');
    if (now) {
      var btn = now.querySelector('[data-flit-now-btn]');
      var input = now.querySelector('[data-flit-now-input]');
      var result = now.querySelector('[data-flit-now-result]');
      if (btn && input && result) {
        btn.addEventListener('click', function () {
          var pin = (input.value || '').trim();
          if (pin.length < 6) { result.hidden = false; result.style.color = ''; result.textContent = 'Enter a valid 6-digit pincode.'; return; }
          result.hidden = false;
          result.textContent = '✓ Delivers to ' + pin + ' by tomorrow 3 PM';
        });
      }
    }
  }

  /* ---- Money formatting (INR fallback; cart.js gives cents) ---- */
  function money(cents) {
    var n = (cents || 0) / 100;
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  /* ---- Body scroll lock for overlays ---- */
  function lockScroll(on) { document.documentElement.classList.toggle('flit-noscroll', !!on); }

  /* ============================ Wishlist (localStorage) ============================ */
  var WKEY = 'flit:wishlist';
  var Wish = {
    read: function () { try { return JSON.parse(localStorage.getItem(WKEY) || '[]'); } catch (e) { return []; } },
    write: function (list) {
      localStorage.setItem(WKEY, JSON.stringify(list));
      document.dispatchEvent(new CustomEvent('flit:wishlist-change'));
    },
    has: function (id) { return this.read().some(function (i) { return String(i.id) === String(id); }); },
    toggle: function (item) {
      var list = this.read();
      var i = list.findIndex(function (x) { return String(x.id) === String(item.id); });
      if (i > -1) list.splice(i, 1); else list.unshift(item);
      this.write(list);
      return i === -1; // true if added
    },
    remove: function (id) {
      this.write(this.read().filter(function (i) { return String(i.id) !== String(id); }));
    },
    count: function () { return this.read().length; }
  };

  function updateWishCounts() {
    var c = Wish.count();
    document.querySelectorAll('[data-flit-wish-count]').forEach(function (el) { el.textContent = c; });
    document.querySelectorAll('[data-flit-wish-dot]').forEach(function (el) { el.hidden = c === 0; });
  }

  function syncWishButtons(scope) {
    (scope || document).querySelectorAll('[data-flit-wish]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', String(Wish.has(btn.getAttribute('data-wish-id'))));
    });
  }

  function initWishlist(btn) {
    if (btn.__flitWishBound) return;
    btn.__flitWishBound = true;
    btn.setAttribute('aria-pressed', String(Wish.has(btn.getAttribute('data-wish-id'))));
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var added = Wish.toggle({
        id: btn.getAttribute('data-wish-id'),
        pid: btn.getAttribute('data-wish-pid'),
        title: btn.getAttribute('data-wish-title'),
        price: btn.getAttribute('data-wish-price'),
        image: btn.getAttribute('data-wish-image'),
        url: btn.getAttribute('data-wish-url')
      });
      btn.setAttribute('aria-pressed', String(added));
    });
  }

  /* ============================ Cart (AJAX) ============================ */
  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-flit-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          if (el.matches('.flit-bottomnav__badge')) el.hidden = cart.item_count === 0;
        });
        renderCartPanel(cart);
        return cart;
      })
      .catch(function () {});
  }

  function renderCartPanel(cart) {
    var body = document.querySelector('[data-flit-cart-body]');
    var foot = document.querySelector('[data-flit-cart-foot]');
    if (!body) return;
    if (!cart.items || !cart.items.length) {
      body.innerHTML = '<p class="flit-bag__empty">Your bag is empty.</p>';
      if (foot) foot.hidden = true;
      return;
    }
    body.innerHTML = cart.items.map(function (it) {
      var opts = (it.options_with_values || []).filter(function (o) { return o.value && o.value !== 'Default Title'; })
        .map(function (o) { return o.value; }).join(' · ');
      return '' +
        '<div class="flit-bagline" data-line-key="' + it.key + '">' +
          '<img class="flit-bagline__img" src="' + (it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : '') + '" alt="" width="56" height="56">' +
          '<div class="flit-bagline__info">' +
            '<a class="flit-bagline__title" href="' + it.url + '">' + it.product_title + '</a>' +
            (opts ? '<span class="flit-bagline__opts">' + opts + '</span>' : '') +
            '<div class="flit-bagline__qty">' +
              '<button type="button" class="flit-bagline__step" data-flit-qty="dec" aria-label="Decrease">–</button>' +
              '<span>' + it.quantity + '</span>' +
              '<button type="button" class="flit-bagline__step" data-flit-qty="inc" aria-label="Increase">+</button>' +
            '</div>' +
          '</div>' +
          '<div class="flit-bagline__right">' +
            '<span class="flit-bagline__price">' + money(it.final_line_price) + '</span>' +
            '<button type="button" class="flit-bagline__remove" data-flit-qty="rm" aria-label="Remove">Remove</button>' +
          '</div>' +
        '</div>';
    }).join('');
    var sub = document.querySelector('[data-flit-cart-subtotal]');
    if (sub) sub.textContent = money(cart.total_price);
    if (foot) foot.hidden = false;
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); }).then(function (cart) {
      document.querySelectorAll('[data-flit-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
        if (el.matches('.flit-bottomnav__badge')) el.hidden = cart.item_count === 0;
      });
      renderCartPanel(cart);
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'flit-bag', cartData: cart });
      }
    });
  }

  function renderWishPanel() {
    var body = document.querySelector('[data-flit-wish-body]');
    if (!body) return;
    var list = Wish.read();
    if (!list.length) {
      body.innerHTML = '<p class="flit-bag__empty">Your wishlist is empty. Tap the heart on any product to save it.</p>';
      return;
    }
    body.innerHTML = list.map(function (it) {
      return '' +
        '<div class="flit-bagline" data-wish-row="' + it.id + '">' +
          '<a href="' + it.url + '"><img class="flit-bagline__img" src="' + (it.image || '') + '" alt="" width="56" height="56"></a>' +
          '<div class="flit-bagline__info">' +
            '<a class="flit-bagline__title" href="' + it.url + '">' + it.title + '</a>' +
            '<span class="flit-bagline__price">' + (it.price || '') + '</span>' +
          '</div>' +
          '<div class="flit-bagline__right">' +
            '<button type="button" class="flit-pill flit-wishmove" data-wish-move="' + it.id + '">Move to bag</button>' +
            '<button type="button" class="flit-bagline__remove" data-wish-remove="' + it.id + '">Remove</button>' +
          '</div>' +
        '</div>';
    }).join('');
  }

  /* ============================ Bag drawer ============================ */
  function initBagDrawer() {
    var bag = document.querySelector('[data-flit-bag]');
    if (!bag || bag.__flitBound) return;
    bag.__flitBound = true;

    function open(tab) {
      bag.hidden = false;
      lockScroll(true);
      requestAnimationFrame(function () { bag.classList.add('is-open'); });
      switchTab(tab || 'cart');
      refreshCart();
      renderWishPanel();
    }
    function close() { bag.classList.remove('is-open'); lockScroll(false); setTimeout(function () { bag.hidden = true; }, 250); }
    function switchTab(name) {
      bag.querySelectorAll('[data-bag-tab]').forEach(function (t) {
        var on = t.getAttribute('data-bag-tab') === name;
        t.classList.toggle('is-active', on); t.setAttribute('aria-selected', String(on));
      });
      bag.querySelectorAll('[data-bag-panel]').forEach(function (p) {
        p.hidden = p.getAttribute('data-bag-panel') !== name;
      });
      if (name === 'wishlist') renderWishPanel();
    }

    document.querySelectorAll('[data-flit-bag-open]').forEach(function (b) {
      b.addEventListener('click', function () { open('cart'); });
    });
    bag.querySelectorAll('[data-flit-bag-close]').forEach(function (b) { b.addEventListener('click', close); });
    bag.querySelectorAll('[data-bag-tab]').forEach(function (t) {
      t.addEventListener('click', function () { switchTab(t.getAttribute('data-bag-tab')); });
    });

    // Delegated actions inside the bag
    bag.addEventListener('click', function (e) {
      var qbtn = e.target.closest('[data-flit-qty]');
      if (qbtn) {
        var line = qbtn.closest('[data-line-key]');
        if (!line) return;
        var key = line.getAttribute('data-line-key');
        var qty = parseInt(line.querySelector('.flit-bagline__qty span').textContent, 10) || 1;
        var action = qbtn.getAttribute('data-flit-qty');
        if (action === 'inc') changeLine(key, qty + 1);
        else if (action === 'dec') changeLine(key, Math.max(0, qty - 1));
        else if (action === 'rm') changeLine(key, 0);
        return;
      }
      var mv = e.target.closest('[data-wish-move]');
      if (mv) {
        var id = mv.getAttribute('data-wish-move');
        fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: id, quantity: 1 })
        }).then(function (r) { return r.json(); }).then(function () {
          Wish.remove(id); renderWishPanel(); refreshCart();
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'flit-bag' });
          switchTab('cart');
        }).catch(function () {});
        return;
      }
      var rm = e.target.closest('[data-wish-remove]');
      if (rm) { Wish.remove(rm.getAttribute('data-wish-remove')); renderWishPanel(); }
    });

    bag.__open = open;
  }

  /* ============================ Search overlay ============================ */
  function initSearchOverlay() {
    var ov = document.querySelector('[data-flit-search]');
    if (!ov || ov.__flitBound) return;
    ov.__flitBound = true;
    var input = ov.querySelector('#flit-search-input');
    function open() {
      ov.hidden = false; lockScroll(true);
      requestAnimationFrame(function () { ov.classList.add('is-open'); if (input) input.focus(); });
    }
    function close() { ov.classList.remove('is-open'); lockScroll(false); setTimeout(function () { ov.hidden = true; }, 200); }
    document.querySelectorAll('[data-flit-search-open]').forEach(function (b) { b.addEventListener('click', open); });
    ov.querySelectorAll('[data-flit-search-close]').forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && ov.classList.contains('is-open')) close(); });
    ov.__open = open;
  }

  function boot(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-flit-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-flit-carousel] .flit-rail, [data-flit-track]').forEach(initProgress);
    scope.querySelectorAll('.flit-accordion').forEach(initAccordion);
    scope.querySelectorAll('[data-flit-rotate]').forEach(initRotatingPlaceholder);
    scope.querySelectorAll('[data-flit-cardimgs]').forEach(initCardImages);
    scope.querySelectorAll('[data-flit-pdp]').forEach(initPdp);
    scope.querySelectorAll('[data-flit-wish]').forEach(initWishlist);
    // Global singletons
    initBagDrawer();
    initSearchOverlay();
    updateWishCounts();
    // One-time global wiring
    if (!boot.__globals) {
      boot.__globals = true;
      document.addEventListener('flit:wishlist-change', function () {
        updateWishCounts(); syncWishButtons(); renderWishPanel();
      });
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { refreshCart(); });
      }
    }
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });

  // Re-init when sections are re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  window.FLIT = { boot: boot };
})();
